/**
 * 📅 EventDateClassifier — تصنيف تاريخ المناسبة (KSA)
 *
 * منفذ TypeScript مطابق لخدمة Flutter `EventDateClassifier`، عشان الداشبورد
 * والتطبيق يصنّفون نفس التاريخ بنفس النتيجة (dayType + season) → فيختارون
 * نفس الباقة المطابقة.
 *
 * يقرأ بيانات تقويم السعودية مرة وحدة من Firestore ويخزّنها في الذاكرة:
 *
 *   calendar_config/holidays_eids
 *     { "2026-06-07": { type: "eid",     label: "عيد الأضحى" },
 *       "2026-06-18": { type: "holiday", label: "اليوم الوطني" }, ... }
 *     (أو نفس الخريطة داخل حقل special_days)
 *
 *   calendar_config/season_ranges
 *     { summer_ranges: [ { start: "2026-06-20", end: "2026-08-13" } ] }
 *
 * أولوية الموسم (الأعلى → الأدنى): eid → holiday → summer → school
 *
 * قاعدة نوع اليوم (مطابقة Flutter):
 *   الخميس/الجمعة → weekend، وكل يوم آخر → weekday
 */

import { collection, doc, getDoc } from 'firebase/firestore';
import { db } from '@infrastructure/firebase/config';
import {
  DayType,
  Season,
  DayTypeLabels,
  dayOfWeekToDayType,
  type DayTypeValue,
  type SeasonValue,
} from '@domain/value-objects/PackageEnums';

const COLLECTION = 'calendar_config';
const HOLIDAYS_DOC = 'holidays_eids';
const SEASON_RANGES_DOC = 'season_ranges';

type SpecialDayType = 'eid' | 'holiday';

interface SpecialDay {
  type: SpecialDayType;
  label: string;
}

interface SummerRange {
  start: string; // YYYY-MM-DD
  end: string; // YYYY-MM-DD
}

export interface DateClassification {
  dayType: DayTypeValue;
  season: SeasonValue;
  /** وصف عربي مختصر — مثل "عيد الأضحى • نهاية الأسبوع" */
  label: string;
}

class EventDateClassifier {
  /** "YYYY-MM-DD" → SpecialDay */
  private specialDays = new Map<string, SpecialDay>();
  private summerRanges: SummerRange[] = [];
  private loadPromise: Promise<void> | null = null;
  private loaded = false;

  /**
   * يحمّل بيانات التقويم مرة وحدة (idempotent). يُستدعى ضمنياً من classify،
   * أو صراحةً للتحميل المسبق.
   */
  async ensureLoaded(): Promise<void> {
    if (this.loaded) return;
    if (!this.loadPromise) {
      this.loadPromise = this.load();
    }
    await this.loadPromise;
  }

  /** يعيد تحميل الإعدادات بعد تعديلها من صفحة السوبر أدمن. */
  async refresh(): Promise<void> {
    this.specialDays.clear();
    this.summerRanges = [];
    this.loaded = false;
    this.loadPromise = null;
    await this.ensureLoaded();
  }

  private async load(): Promise<void> {
    try {
      await Promise.all([this.loadHolidaysAndEids(), this.loadSeasonRanges()]);
      // eslint-disable-next-line no-console
      console.log(
        `[EventDateClassifier] loaded ${this.specialDays.size} special days, ${this.summerRanges.length} summer ranges`
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[EventDateClassifier] load error — fallback to school/weekday:', err);
    } finally {
      this.loaded = true;
    }
  }

  private async loadHolidaysAndEids(): Promise<void> {
    const snap = await getDoc(doc(collection(db, COLLECTION), HOLIDAYS_DOC));
    if (!snap.exists()) return;
    const data = snap.data();
    if (!data) return;

    // ندعم شكلين: المفاتيح في الجذر مباشرة، أو داخل حقل special_days.
    const source =
      data['special_days'] && typeof data['special_days'] === 'object'
        ? (data['special_days'] as Record<string, unknown>)
        : (data as Record<string, unknown>);

    for (const [key, value] of Object.entries(source)) {
      if (!isDateString(key)) continue; // نتجاهل حقول مثل updatedAt/version
      const parsed = parseSpecialDay(value);
      if (parsed) this.specialDays.set(key, parsed);
    }
  }

  private async loadSeasonRanges(): Promise<void> {
    const snap = await getDoc(doc(collection(db, COLLECTION), SEASON_RANGES_DOC));
    if (!snap.exists()) return;
    const raw = snap.data()?.['summer_ranges'];
    if (!Array.isArray(raw)) return;

    for (const r of raw) {
      if (r && typeof r === 'object') {
        const start = (r as Record<string, unknown>)['start'];
        const end = (r as Record<string, unknown>)['end'];
        if (isDateString(start) && isDateString(end)) {
          this.summerRanges.push({ start, end });
        }
      }
    }
  }

  /**
   * يصنّف التاريخ إلى dayType + season حسب قواعد السعودية.
   *
   * متزامن (synchronous) — يعتمد على الكاش. استدعِ ensureLoaded() أولاً.
   * يرجع دايماً نتيجة: fallback = موسم دراسي + نوع اليوم الحقيقي لو ما فيه بيانات.
   *
   * @param date تاريخ بصيغة 'YYYY-MM-DD' أو كائن Date
   */
  classify(date: string | Date): DateClassification {
    const { dateStr, dayType } = this.normalize(date);

    const special = this.specialDays.get(dateStr);

    // أولوية 1 — عيد
    if (special?.type === 'eid') {
      return {
        dayType,
        season: Season.EID,
        label: `${special.label} • ${DayTypeLabels[dayType]}`,
      };
    }

    // أولوية 2 — إجازة رسمية
    if (special?.type === 'holiday') {
      return {
        dayType,
        season: Season.HOLIDAY,
        label: `${special.label} • ${DayTypeLabels[dayType]}`,
      };
    }

    // أولوية 3 — إجازة صيفية
    for (const range of this.summerRanges) {
      if (dateStr >= range.start && dateStr <= range.end) {
        return {
          dayType,
          season: Season.SUMMER,
          label: `إجازة صيفية • ${DayTypeLabels[dayType]}`,
        };
      }
    }

    // أولوية 4 — موسم دراسي (افتراضي)
    return {
      dayType,
      season: Season.SCHOOL,
      label: `موسم دراسي • ${DayTypeLabels[dayType]}`,
    };
  }

  private normalize(date: string | Date): { dateStr: string; dayType: DayTypeValue } {
    let dateStr: string;
    let dow: number;
    if (typeof date === 'string') {
      dateStr = date;
      dow = new Date(date + 'T00:00:00').getDay();
    } else {
      dateStr = toDateStr(date);
      dow = date.getDay();
    }
    return { dateStr, dayType: dayOfWeekToDayType(dow) };
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────

function parseSpecialDay(value: unknown): SpecialDay | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  const type = v['type'];
  if (type !== 'eid' && type !== 'holiday') return null;
  const label = typeof v['label'] === 'string' ? (v['label'] as string) : '';
  return { type, label };
}

function isDateString(key: unknown): key is string {
  if (typeof key !== 'string' || key.length !== 10) return false;
  if (key[4] !== '-' || key[7] !== '-') return false;
  return /^\d{8}$/.test(key.replace(/-/g, ''));
}

function toDateStr(d: Date): string {
  return (
    `${d.getFullYear()}-` +
    `${String(d.getMonth() + 1).padStart(2, '0')}-` +
    `${String(d.getDate()).padStart(2, '0')}`
  );
}

export const eventDateClassifier = new EventDateClassifier();

// نوع DayType مُعاد التصدير للراحة (مستخدم في فلترة الباقات)
export { DayType, Season };
